import xlsx from 'xlsx';

/**
 * Motor genérico de exportación a Excel
 * Usa una Strategy para obtener la configuración específica de cada reporte
 */
export default class ExcelExporter {
  constructor(strategy, data, context) {
    this.strategy = strategy;
    this.data = data;
    this.context = context;
  }

  /**
   * Genera y descarga el archivo Excel
   */
  generate() {
    const columns = this.strategy.getVisibleColumns({
      reportType: this.context.esOperacion,
      includeSubtotals: true,
      maxDesembolsos: this.context.maxDesembolsos,
      columnasMeses: this.context.columnasMeses,  // ✅ Pasar columnas de meses para Info Cobranzas Proyectadas
      columnasPeriodos: this.context.columnasPeriodos,  // ✅ Pasar columnas de períodos para Info Cobranzas
      datos: this.data  // ✅ Pasar datos para fallback
    });

    const excelData = this.buildExcelData(columns);
    const ws = this.createWorksheet(excelData, columns);
    const wb = this.createWorkbook(ws);

    // Descargar
    xlsx.writeFile(wb, this.strategy.getFileName('xlsx'));
  }

  /**
   * Construye los datos para Excel (array de arrays)
   * @param {Array} columns
   * @returns {Array}
   */
  buildExcelData(columns) {
    const metadata = this.strategy.getMetadata(this.data, this.context);
    const rows = [];

    // Fila 1: Título
    const titleRow = Array(columns.length).fill('');
    titleRow[0] = metadata.title;
    rows.push(titleRow);

    // Fila 2: Subtítulo (fideicomisos) si existe
    if (metadata.subtitle) {
      const subtitleRow = Array(columns.length).fill('');
      subtitleRow[0] = metadata.subtitle;
      rows.push(subtitleRow);
    }

    // Fila 3: Operatorias/Líneas si existe
    if (metadata.operatorias) {
      const operatoriasRow = Array(columns.length).fill('');
      operatoriasRow[0] = metadata.operatorias;
      rows.push(operatoriasRow);
    }

    // Fila siguiente: Periodo (solo para HTC)
    if (metadata.periodo) {
      const periodoRow = Array(columns.length).fill('');
      periodoRow[0] = `Periodo: ${metadata.periodo}`;
      rows.push(periodoRow);
    }

    // Fila 4 (o siguiente): Fecha de consulta
    const consultaRow = Array(columns.length).fill('');
    consultaRow[0] = `Fecha de consulta: ${metadata.fechaConsulta}`;
    rows.push(consultaRow);

    // Fila 5 (o siguiente): Fecha de generación
    const generacionRow = Array(columns.length).fill('');
    generacionRow[0] = `Generado el: ${metadata.fechaGeneracion}`;
    rows.push(generacionRow);

    // Fila vacía de separación
    rows.push(Array(columns.length).fill(''));

    // Fila de headers
    const headersRow = columns.map(col => col.header);
    rows.push(headersRow);

    // Filas de datos
    const displayData = this.strategy.getDisplayData(this.data);
    const regularData = this.strategy.getRegularData(this.data);

    displayData.forEach(item => {
      const row = columns.map(col => this.transformCell(item, col));
      rows.push(row);
    });

    // Fila de totales
    const totalRow = columns.map(col => this.transformTotalCell(regularData, col));
    rows.push(totalRow);

    return rows;
  }

  /**
   * Transforma una celda individual
   * @param {Object} item
   * @param {Object} column
   * @returns {*}
   */
  transformCell(item, column) {
    let result;

    if (item._esSubtotal && column.formatSubtotal) {
      result = column.formatSubtotal(item, this.context, this.data);
    } else {
      result = column.format(item, this.context);
    }

    // Para Excel, usar el valor raw (numérico) cuando esté disponible
    return this.strategy.getRawValue(result);
  }

  /**
   * Transforma una celda de la fila de totales
   * @param {Array} regularData
   * @param {Object} column
   * @returns {*}
   */
  transformTotalCell(regularData, column) {
    let result;

    if (column.formatTotal) {
      result = column.formatTotal(regularData, this.context);
    } else if (column.calculateTotal) {
      const totalValue = column.calculateTotal(regularData, this.context);
      // Para totales en Excel, devolver el valor numérico directamente
      return totalValue;
    } else {
      result = '';
    }

    return this.strategy.getRawValue(result);
  }

  /**
   * Crea el worksheet de Excel
   * @param {Array} excelData
   * @param {Array} columns
   * @returns {Object}
   */
  createWorksheet(excelData, columns) {
    const ws = xlsx.utils.aoa_to_sheet(excelData);

    // Configurar anchos de columna
    ws['!cols'] = this.createColumnWidths(columns);

    // Aplicar estilos (si la librería lo soporta)
    // Nota: xlsx básico no soporta estilos, necesitarías xlsx-style o similar
    // Por ahora, dejamos la configuración de anchos

    return ws;
  }

  /**
   * Crea la configuración de anchos de columna
   * @param {Array} columns
   * @returns {Array}
   */
  createColumnWidths(columns) {
    return columns.map(col => ({
      wch: col.excelWidth || Math.max(col.header.length, 12)
    }));
  }

  /**
   * Crea el workbook de Excel
   * @param {Object} ws
   * @returns {Object}
   */
  createWorkbook(ws) {
    const wb = xlsx.utils.book_new();
    const title = this.strategy.getMetadata(this.data, this.context).title;
    // Truncar a 31 caracteres, límite de Excel para nombres de hoja
    const sheetName = title.substring(0, 31);
    xlsx.utils.book_append_sheet(wb, ws, sheetName);
    return wb;
  }
}
